import type React from "react";
import { useState } from "react";

// One chip per statement Superdoc just committed to memory ("Remembered: you
// prefer the metabolic panel"). Undo dispatches `memory_undo` for that entry
// and flips the chip to "Forgotten" locally — the entry is retired server-side,
// but the chip stays in the thread so the clinician can see what was (and is
// no longer) remembered. Undo hides once the backend's undo window has passed,
// once the entry is already undone, or on a frozen card from an earlier turn.
//
// The mirror image: an entry the clinician asked to forget in chat arrives
// with `forgotten` and renders "Forgotten:" with an Undo that dispatches
// `memory_restore` and flips it back to "Remembered:" (`restored`).
export interface MemorySavedEntry {
  entry_id: string;
  statement: string;
  scope: "user" | "user_patient" | "clinic";
  category?: string;
  undo_until?: string | null;
  undone?: boolean;
  forgotten?: boolean;
  restore_until?: string | null;
  restored?: boolean;
}

export interface MemorySavedData {
  entries: MemorySavedEntry[];
  anchor_message_id?: string | null;
}

interface MemorySavedProps {
  data: MemorySavedData;
  onCustomAction: (action: { type: string; payload?: Record<string, unknown> }) => void;
  disabled?: boolean;
  // This card belongs to an earlier turn — render it read-only.
  frozen?: boolean;
}

const BookmarkIcon = ({ muted }: { muted?: boolean }) => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, display: "block" }}>
    <path
      d="M4 2.5h8a.5.5 0 0 1 .5.5v10.5L8 10.5l-4.5 3V3a.5.5 0 0 1 .5-.5Z"
      fill={muted ? "#F3F4F6" : "#EEF2FF"}
      stroke={muted ? "#9CA3AF" : "#4F46E5"}
      strokeWidth="1.3"
      strokeLinejoin="round"
    />
  </svg>
);

const undoExpired = (undoUntil?: string | null): boolean => {
  if (!undoUntil) return false;
  const ts = Date.parse(undoUntil);
  return !Number.isNaN(ts) && ts < Date.now();
};

const MemorySaved: React.FC<MemorySavedProps> = ({ data, onCustomAction, disabled, frozen }) => {
  const entries = (data?.entries ?? []).filter((e) => !!e?.entry_id && !!e?.statement?.trim());
  // Entries toggled in this session: undone "Remembered" chips and restored
  // "Forgotten" chips (one set — an entry is only ever one or the other).
  const [toggledIds, setToggledIds] = useState<Set<string>>(() => new Set());

  if (!entries.length) return null;

  const undo = (entry: MemorySavedEntry) => {
    if (disabled || frozen) return;
    onCustomAction({
      type: entry.forgotten ? "memory_restore" : "memory_undo",
      payload: { entry_id: entry.entry_id },
    });
    setToggledIds((prev) => new Set(prev).add(entry.entry_id));
  };

  return (
    <div style={styles.container}>
      {entries.map((entry) => {
        const toggled = toggledIds.has(entry.entry_id);
        // What the chip says now: a "Remembered" chip reads Forgotten once
        // undone; a "Forgotten" chip reads Remembered once restored.
        const undone = entry.forgotten ? !(entry.restored || toggled) : !!entry.undone || toggled;
        const settled = entry.forgotten ? !!entry.restored || toggled : !!entry.undone || toggled;
        const window = entry.forgotten ? entry.restore_until : entry.undo_until;
        const canUndo = !settled && !frozen && !undoExpired(window);
        return (
          <div key={entry.entry_id} style={{ ...styles.chip, ...(undone ? styles.chipUndone : {}) }}>
            <BookmarkIcon muted={undone} />
            <span style={{ ...styles.text, ...(undone ? styles.textUndone : {}) }}>
              <span style={styles.label}>{undone ? "Forgotten:" : "Remembered:"}</span> {entry.statement}
            </span>
            {canUndo && (
              <button
                type="button"
                disabled={disabled}
                onClick={() => undo(entry)}
                style={{ ...styles.undo, ...(disabled ? styles.undoDisabled : {}) }}
              >
                Undo
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    marginTop: "8px",
    // Ancestors center-align text; every widget defends itself.
    textAlign: "left",
  },
  chip: {
    display: "inline-flex",
    alignItems: "flex-start",
    gap: "6px",
    padding: "5px 9px",
    borderRadius: "999px",
    border: "1px solid #C7D2FE",
    backgroundColor: "#F5F3FF",
    alignSelf: "flex-start",
    maxWidth: "100%",
  },
  chipUndone: { border: "1px solid #E5E7EB", backgroundColor: "#F9FAFB" },
  text: { fontSize: "12px", color: "#374151", lineHeight: 1.4, minWidth: 0 },
  textUndone: { color: "#9CA3AF", textDecoration: "line-through" },
  label: { fontWeight: 600, color: "#4338CA" },
  undo: {
    flexShrink: 0,
    padding: 0,
    border: "none",
    background: "none",
    fontSize: "12px",
    fontWeight: 500,
    color: "#4F46E5",
    textDecoration: "underline",
    cursor: "pointer",
    fontFamily: "inherit",
    lineHeight: 1.4,
  },
  undoDisabled: { opacity: 0.6, cursor: "not-allowed" },
};

export default MemorySaved;
